import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import { Button, Empty, Select, Skeleton, Space, Spin, message } from "antd";
import {
  ApartmentOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import MaterialKpiCards from "./MaterialKpiCards";
import MaterialCostCharts from "./MaterialCostCharts";
import MaterialSummaryTable from "./MaterialSummaryTable";
import PhaseCostDrawer from "./PhaseCostDrawer";
import MaterialMonthlyScheduleDrawer from "./MaterialMonthlyScheduleDrawer";
import MaterialTaskBreakdownDrawer from "./MaterialTaskBreakdownDrawer";
import { MaterialSummaryData, MaterialSummaryRow, num } from "./materialShared";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";

// Project-level material cost analysis: distinct materials aggregated across
// task BOQ lines, KPI cards + top-5 charts + full register. Project context
// comes the same way as ThreeMConsolidation: location.state { id, projectName }
// with the optional :projectId route param as fallback.

const MaterialCostAnalysisReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<MaterialSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);

  // Phase/Activity dropdown options come from the unfiltered 3M consolidation
  // tree (same source the 3M report uses), so the material summary payload —
  // which carries no phase/activity info — never has to.
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [phaseDrawerOpen, setPhaseDrawerOpen] = useState(false);

  // Org branding for the Excel export. The currentUser slice stores the raw
  // /users/currentUser payload; orgName/orgLogo exist at runtime even though
  // the slice's local Data interface doesn't declare them.
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;
  // Per-material task drill-down (phase → activity → task); null = closed.
  const [breakdownMaterial, setBreakdownMaterial] =
    useState<MaterialSummaryRow | null>(null);
  // Per-material month-by-month schedule; null = closed. Mutually exclusive
  // with the task drill-down — antd stacks drawers at one z-index, so two open
  // at once just looks broken.
  const [scheduleMaterial, setScheduleMaterial] =
    useState<MaterialSummaryRow | null>(null);

  // Project top-menu, same as WBSViewNew / ThreeMConsolidation
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter options: one unfiltered consolidation load per project.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) => {
        // Non-fatal: the report still renders, only the filters stay empty.
        console.error("Error loading phase/activity filter options:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.get3MMaterialSummary(
          projectId,
          { phaseId, activityId }
        );
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load material summary");
          message.error(response?.message || "Failed to load material summary");
        }
      } catch (err) {
        console.error("Error loading material cost analysis:", err);
        if (!cancelled) {
          setError("Error loading Material Cost Analysis");
          message.error("Error loading Material Cost Analysis");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const materials = useMemo(() => data?.materials || [], [data]);

  // Grand total for shares: trust the API value, fall back to the row sum
  // (gRPC may omit zero/absent fields).
  const grandTotal = useMemo(() => {
    const apiTotal = num(data?.totalCost);
    if (apiTotal > 0) return apiTotal;
    return materials.reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, materials]);

  const wbsName = useWbsName();
  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases]
  );

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId;

  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
  };

  // Styled multi-sheet Excel export: fetches the all-materials task breakdown
  // for the current filters, then hands everything to the lazily-loaded
  // exceljs builder. The logo fetch is best-effort (cross-origin blob).
  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const [breakdownResponse, monthResponse, helpers] = await Promise.all([
        threeMReportApis.get3MMaterialTaskBreakdownAll(projectId, {
          phaseId,
          activityId,
        }),
        threeMReportApis.get3MMaterialMonthlyScheduleAll(projectId, {
          phaseId,
          activityId,
        }),
        import("../shared/excelHelpers"),
      ]);
      if (!breakdownResponse?.status) {
        message.error(
          breakdownResponse?.message || "Failed to load export data"
        );
        return;
      }
      // The month grid is additive: a project with no task schedules still
      // gets the other five sheets rather than a failed export.
      if (!monthResponse?.status) {
        console.warn(
          "Month-wise sheet skipped:",
          monthResponse?.message || "monthly schedule fetch failed"
        );
      }
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)
              ?.activityName
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        grandTotal,
        breakdownRows: breakdownResponse?.data?.rows || [],
        monthCells: monthResponse?.status ? monthResponse.data?.rows || [] : [],
        orgName,
        logo,
        filters: { phaseName, activityName },
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Material Cost Analysis:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Head title="Material Cost Analysis" />
      <Content>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Material Cost Analysis
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Material cost rollup across task BOQ lines
                </span>
              </div>
              <Space>
                <Button
                  icon={<ApartmentOutlined />}
                  onClick={() => setPhaseDrawerOpen(true)}
                  disabled={basePhases.length === 0}
                >
                  Phase Costs
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || materials.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <MaterialKpiCards data={data} grandTotal={grandTotal} />
                {materials.length === 0 ? (
                  <Empty
                    description={
                      hasFilters
                        ? "No materials match the current filters"
                        : "No material cost data for this project yet"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <>
                    <MaterialCostCharts
                      materials={materials}
                      grandTotal={grandTotal}
                    />
                    <MaterialSummaryTable
                      materials={materials}
                      grandTotal={grandTotal}
                      onTasksClick={(row) => {
                        setScheduleMaterial(null);
                        setBreakdownMaterial(row);
                      }}
                      onMaterialClick={(row) => {
                        setBreakdownMaterial(null);
                        setScheduleMaterial(row);
                      }}
                    />
                  </>
                )}
              </Spin>
            )}
          </div>
        </Card>
      </Content>

      <PhaseCostDrawer
        open={phaseDrawerOpen}
        phases={basePhases}
        onClose={() => setPhaseDrawerOpen(false)}
      />

      <MaterialTaskBreakdownDrawer
        open={!!breakdownMaterial}
        projectId={projectId}
        materialId={breakdownMaterial?.materialId}
        materialName={breakdownMaterial?.materialName}
        uomName={breakdownMaterial?.uomName}
        phaseId={phaseId}
        activityId={activityId}
        onClose={() => setBreakdownMaterial(null)}
      />

      <MaterialMonthlyScheduleDrawer
        open={!!scheduleMaterial}
        projectId={projectId}
        materialId={scheduleMaterial?.materialId}
        materialName={scheduleMaterial?.materialName}
        materialCode={scheduleMaterial?.materialCode}
        uomName={scheduleMaterial?.uomName}
        phaseId={phaseId}
        activityId={activityId}
        onClose={() => setScheduleMaterial(null)}
      />
    </>
  );
};

export default MaterialCostAnalysisReport;
